import type { Job } from '@/domains/jobs/components/JobCard';
import { format, parseISO } from 'date-fns';

export interface DailyCapacity {
  date: string;
  totalHours: number;
  availableHours: number;
  utilizationPercent: number;
  isOverallocated: boolean;
  jobs: Job[];
}

/**
 * Calculate capacity and utilization for a team across a date range
 * @param jobs - All jobs assigned to this team
 * @param rangeStart - Start of the date range
 * @param totalDays - Number of days in the range
 * @param hoursPerDay - Available hours per day (default 8)
 */
export function calculateTeamCapacity(
  jobs: Job[],
  rangeStart: Date,
  totalDays: number,
  hoursPerDay = 8,
): Map<string, DailyCapacity> {
  const capacityMap = new Map<string, DailyCapacity>();

  // Initialize all days in range
  for (let i = 0; i < totalDays; i++) {
    const date = new Date(rangeStart);
    date.setDate(date.getDate() + i);
    const dateStr = format(date, 'yyyy-MM-dd');

    capacityMap.set(dateStr, {
      date: dateStr,
      totalHours: 0,
      availableHours: hoursPerDay,
      utilizationPercent: 0,
      isOverallocated: false,
      jobs: [],
    });
  }

  // Calculate hours for each job
  jobs.forEach((job) => {
    if (!job.scheduledDate) return;

    // If job has startTime and endTime, calculate actual hours
    if (job.startTime && job.endTime) {
      const startTime = parseISO(job.startTime);
      const endTime = parseISO(job.endTime);

      // Calculate which days this job spans
      const jobStartDate = new Date(startTime);
      jobStartDate.setHours(0, 0, 0, 0);

      const jobEndDate = new Date(endTime);
      jobEndDate.setHours(0, 0, 0, 0);

      // Allocate hours to each day the job spans
      let currentDate = new Date(jobStartDate);
      const totalMs = endTime.getTime() - startTime.getTime();
      const totalHours = totalMs / (1000 * 60 * 60);

      while (currentDate <= jobEndDate) {
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        const capacity = capacityMap.get(dateStr);

        if (capacity) {
          // Calculate hours for this specific day
          const dayStart = new Date(currentDate);
          dayStart.setHours(0, 0, 0, 0);
          const dayEnd = new Date(currentDate);
          dayEnd.setHours(23, 59, 59, 999);

          const effectiveStart = startTime > dayStart ? startTime : dayStart;
          const effectiveEnd = endTime < dayEnd ? endTime : dayEnd;

          const hoursThisDay =
            (effectiveEnd.getTime() - effectiveStart.getTime()) / (1000 * 60 * 60);

          if (hoursThisDay > 0) {
            capacity.totalHours += hoursThisDay;
            capacity.jobs.push(job);
          }
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else {
      // Fall back to scheduled date + estimated hours
      const dateStr = job.scheduledDate;
      const capacity = capacityMap.get(dateStr);

      if (capacity) {
        const hours = job.duration || job.estimatedHours || 0;
        capacity.totalHours += hours;
        capacity.jobs.push(job);
      }
    }
  });

  // Calculate utilization percentages
  capacityMap.forEach((capacity) => {
    if (capacity.availableHours > 0) {
      capacity.utilizationPercent = (capacity.totalHours / capacity.availableHours) * 100;
      capacity.isOverallocated = capacity.utilizationPercent > 100;
    } else {
      capacity.utilizationPercent = 0;
      capacity.isOverallocated = false;
    }
  });

  return capacityMap;
}

/**
 * Detect scheduling conflicts (overlapping jobs on same day)
 */
export function detectSchedulingConflicts(jobs: Job[]): Map<string, Job[][]> {
  const conflictMap = new Map<string, Job[][]>();

  // Group jobs by date
  const jobsByDate = new Map<string, Job[]>();
  jobs.forEach((job) => {
    if (!job.scheduledDate || !job.startTime || !job.endTime) return;

    if (!jobsByDate.has(job.scheduledDate)) {
      jobsByDate.set(job.scheduledDate, []);
    }
    jobsByDate.get(job.scheduledDate)!.push(job);
  });

  // Check each day for overlaps
  jobsByDate.forEach((dayJobs, dateStr) => {
    const conflicts: Job[][] = [];

    for (let i = 0; i < dayJobs.length; i++) {
      for (let j = i + 1; j < dayJobs.length; j++) {
        const job1 = dayJobs[i];
        const job2 = dayJobs[j];

        if (!job1.startTime || !job1.endTime || !job2.startTime || !job2.endTime) continue;

        const start1 = parseISO(job1.startTime);
        const end1 = parseISO(job1.endTime);
        const start2 = parseISO(job2.startTime);
        const end2 = parseISO(job2.endTime);

        // Check for overlap: job1 starts before job2 ends AND job1 ends after job2 starts
        if (start1 < end2 && end1 > start2) {
          conflicts.push([job1, job2]);
        }
      }
    }

    if (conflicts.length > 0) {
      conflictMap.set(dateStr, conflicts);
    }
  });

  return conflictMap;
}
